/**
 * ShiftMembersSection - Gestão de membros do turno
 * Design similar ao TeamMembersSection
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/shared/components/ui/Button';
import { FormSection, Select } from '@/shared/components/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { useShiftMembers, useAddShiftMember, useRemoveShiftMember } from '../../hooks';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Icon } from '@/shared/icons'
interface ShiftMembersSectionProps {
  shiftId?: string;
  /** For create mode - stores members locally until shift is created */
  pendingMembers?: Array<{ id: string; name: string; avatar?: string }>;
  onPendingMembersChange?: (members: Array<{ id: string; name: string; avatar?: string }>) => void;
  isCreateMode?: boolean;
}

export const ShiftMembersSection: React.FC<ShiftMembersSectionProps> = ({
  shiftId,
  pendingMembers = [],
  onPendingMembersChange,
  isCreateMode = false,
}) => {
  const { data: members = [], isLoading } = useShiftMembers(isCreateMode ? undefined : shiftId);
  const { data: availableUsers = [] } = useUsersDropdown({ status: 'active' });
  const addMutation = useAddShiftMember();
  const removeMutation = useRemoveShiftMember();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Local state for optimistic updates in edit mode
  const [localMembers, setLocalMembers] = useState(members);
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Use pending members in create mode, local members in edit mode
  const displayMembers = isCreateMode
    ? pendingMembers
    : localMembers.map(m => ({
        id: m.id,
        name: m.name,
        avatar: m.avatar,
      }));

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    const newUser = availableUsers.find(u => u.id === selectedUserId);
    if (!newUser) return;

    if (isCreateMode) {
      // Create mode: only update pending state
      onPendingMembersChange?.([
        ...pendingMembers,
        { id: newUser.id, name: newUser.name, avatar: newUser.avatar },
      ]);
    } else if (shiftId) {
      // Edit mode: update local state + call API
      setLocalMembers([
        ...localMembers,
        {
          id: newUser.id,
          name: newUser.name,
          email: '',
          avatar: newUser.avatar,
          shiftRole: 'primary' as const,
        },
      ]);
      try {
        await addMutation.mutateAsync({
          shiftId,
          data: { userId: selectedUserId, role: 'primary' },
        });
      } catch (error) {
        console.error('Erro ao adicionar membro:', error);
        // Rollback on error
        setLocalMembers(localMembers);
      }
    }

    setShowAddModal(false);
    setSelectedUserId('');
  };

  const handleRemove = async (userId: string) => {
    if (isCreateMode) {
      // Create mode: only update pending state
      onPendingMembersChange?.(pendingMembers.filter(m => m.id !== userId));
    } else if (shiftId) {
      // Edit mode: update local state + call API
      const previousMembers = localMembers;
      setLocalMembers(localMembers.filter(m => m.id !== userId));
      try {
        await removeMutation.mutateAsync({ shiftId, userId });
      } catch (error) {
        console.error('Erro ao remover membro:', error);
        // Rollback on error
        setLocalMembers(previousMembers);
      }
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Filter out users that are already members
  const memberIds = displayMembers.map(m => m.id);
  const availableUsersFiltered = availableUsers.filter(u => !memberIds.includes(u.id));

  return (
    <FormSection
      icon={'users'}
      title="Membros do Turno"
      description="Gerencie os membros que participam deste turno"
    >
      <div className="flex justify-end -mt-2 mb-2">
        <Button onClick={() => setShowAddModal(true)} size="sm">
          <Icon name="user-plus" className="w-4 h-4 mr-2" />
          Adicionar Membro
        </Button>
      </div>
      <div>
        {!isCreateMode && isLoading ? (
          <p style={{ color: 'var(--theme-muted-text)' }}>Carregando...</p>
        ) : displayMembers.length === 0 ? (
          <p style={{ color: 'var(--theme-muted-text)' }}>Nenhum membro adicionado</p>
        ) : (
          <div className="space-y-3">
            {displayMembers.map(member => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--theme-card-hover)' }}
              >
                <div className="flex items-center gap-3">
                  {/* F83: avatar de pessoa = squircle (parear com TeamMembersSection) */}
                  <Avatar className="h-9 w-9" isSquircle>
                    <AvatarImage src={member.avatar} isSquircle />
                    <AvatarFallback isSquircle>{getInitials(member.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {member.name || 'Usuário sem nome'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(member.id)}
                  disabled={removeMutation.isPending}
                >
                  <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {isCreateMode && pendingMembers.length > 0 && (
          <p className="mt-3 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Os membros serão adicionados após a criação do turno
          </p>
        )}

      </div>

        {/* Add Member Modal */}
        {showAddModal && createPortal((
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
          >
            <div
              className="w-full max-w-md p-6 rounded-lg shadow-xl"
              style={{ backgroundColor: '#ffffff' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>
                Adicionar Membro
              </h3>

              <div className="space-y-4">
                {/* Shared Select (NUNCA <select> nativo em modal) */}
                <Select
                  label="Usuário"
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  options={availableUsersFiltered.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="Selecione um usuário"
                  data-testid="shift-members-user-select"
                />

                <div className="flex gap-2 pt-2">
                  <Button onClick={() => setShowAddModal(false)} variant="secondary" className="flex-1">
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAddMember}
                    disabled={!selectedUserId || addMutation.isPending}
                    className="flex-1"
                  >
                    Adicionar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ), document.body)}
    </FormSection>
  );
};

ShiftMembersSection.displayName = 'ShiftMembersSection';
